import React, { useMemo, useState } from 'react';
import { Dayjs } from 'dayjs';
import { SmartScheduleEvent, SmartSchedulePeriod, toSmartScheduleKst } from '@/features/smartSchedule/model';
import AgendaPanel from './AgendaPanel';
import EventCard from './EventCard';
import { buildMonthEventIndex, DAY_NAMES, getMonthGridDays, getStatusGroup } from './utils';

type MonthViewProps = {
    selectedDay: any;
    now: Dayjs;
    events: SmartScheduleEvent[];
    onSelectedDayChange: (date: any) => void;
    onPeriodChange: (period: SmartSchedulePeriod) => void;
    onOpenEvent: (event: SmartScheduleEvent) => void;
};

const MonthView = ({ selectedDay, now, events, onSelectedDayChange, onPeriodChange, onOpenEvent }: MonthViewProps) => {
    const [agendaDate, setAgendaDate] = useState<Dayjs | null>(null);
    const selectedDate = useMemo(() => toSmartScheduleKst(selectedDay), [selectedDay]);
    const days = useMemo(() => getMonthGridDays(selectedDate), [selectedDate]);
    const weeks = useMemo(
        () => Array.from({ length: days.length / 7 }, (_, index) => days.slice(index * 7, index * 7 + 7)),
        [days]
    );
    const eventsByDate = useMemo(() => buildMonthEventIndex(events), [events]);
    const month = selectedDate.month();
    const todayKey = now.format('YYYY-MM-DD');

    const selectDate = (date: Dayjs) => {
        onSelectedDayChange(date);
        onPeriodChange('day');
    };

    const navigateAgendaDay = (offset: -1 | 1) => {
        if (!agendaDate) return;
        const nextDate = agendaDate.add(offset, 'day');
        const firstVisibleDate = days[0];
        const lastVisibleDate = days[days.length - 1];
        if (nextDate.isBefore(firstVisibleDate, 'day') || nextDate.isAfter(lastVisibleDate, 'day')) return;
        setAgendaDate(nextDate);
    };

    return (
        <section className="smart-schedule__month" aria-label={`${selectedDate.format('YYYY년 M월')} 월간 일정`}>
            <div className="smart-schedule__month-calendar">
                <div className="smart-schedule__month-weekdays" role="row">
                    {[1, 2, 3, 4, 5, 6, 0].map((dayIndex) => (
                        <div key={dayIndex} role="columnheader" className={dayIndex === 0 ? 'is-sunday' : dayIndex === 6 ? 'is-saturday' : ''}>
                            {DAY_NAMES[dayIndex]}
                        </div>
                    ))}
                </div>
                <div className="smart-schedule__month-grid" role="grid">
                    {weeks.map((week) => (
                        <div key={week[0].format('YYYY-MM-DD')} className="smart-schedule__month-row" role="row">
                        {week.map((date) => {
                    const dateKey = date.format('YYYY-MM-DD');
                    const dayEvents = eventsByDate.get(dateKey) || [];
                    const hiddenCount = Math.max(0, dayEvents.length - 4);
                    const isToday = dateKey === todayKey;
                    const isSelected = date.isSame(selectedDate, 'day');
                    const isOutside = date.month() !== month;
                    return (
                        <div
                            key={dateKey}
                            role="gridcell"
                            className={[
                                'smart-schedule__month-cell',
                                isOutside ? 'is-outside' : '',
                                isToday ? 'is-today' : '',
                                isSelected ? 'is-selected' : '',
                            ].filter(Boolean).join(' ')}
                            onClick={(clickEvent) => {
                                if ((clickEvent.target as HTMLElement).closest('button')) return;
                                selectDate(date);
                            }}
                        >
                            <button
                                type="button"
                                className="smart-schedule__date-number"
                                aria-label={`${date.format('M월 D일')} 일간 일정 보기, 총 ${dayEvents.length}개 일정`}
                                onClick={() => selectDate(date)}
                            >
                                <span className="smart-schedule__date-day">{date.date()}</span>
                                {isToday && <em>오늘</em>}
                                {!isOutside && dayEvents.length > 0 && (
                                    <span className="smart-schedule__day-total" aria-hidden="true">
                                        총 {dayEvents.length}개
                                    </span>
                                )}
                            </button>
                            {isOutside ? (
                                dayEvents.length > 0 && (
                                    <button
                                        type="button"
                                        className="smart-schedule__outside-count"
                                        aria-label={`${date.format('M월 D일')} 일정 ${dayEvents.length}개 모두 보기`}
                                        onClick={() => setAgendaDate(date)}
                                    >
                                        일정 {dayEvents.length}개
                                    </button>
                                )
                            ) : (
                                <div className="smart-schedule__month-events">
                                    {dayEvents.slice(0, 4).map((event) => (
                                        <EventCard
                                            key={event.id}
                                            event={event}
                                            variant="month"
                                            status={getStatusGroup(event, now)}
                                            onOpen={onOpenEvent}
                                        />
                                    ))}
                                </div>
                            )}
                            {!isOutside && hiddenCount > 0 && (
                                <button
                                    type="button"
                                    className="smart-schedule__more-button"
                                    aria-label={`${date.format('M월 D일')} 숨겨진 일정 ${hiddenCount}개 모두 보기`}
                                    onClick={() => setAgendaDate(date)}
                                >
                                    +{hiddenCount}개 더보기
                                </button>
                            )}
                        </div>
                    );
                        })}
                        </div>
                    ))}
                </div>
            </div>
            {agendaDate && (
                <AgendaPanel
                    title={`${agendaDate.format('M월 D일')} (${DAY_NAMES[agendaDate.day()]})`}
                    events={eventsByDate.get(agendaDate.format('YYYY-MM-DD')) || []}
                    now={now}
                    onClose={() => setAgendaDate(null)}
                    onOpenEvent={onOpenEvent}
                    onNavigateDay={navigateAgendaDay}
                    canNavigatePreviousDay={agendaDate.isAfter(days[0], 'day')}
                    canNavigateNextDay={agendaDate.isBefore(days[days.length - 1], 'day')}
                />
            )}
        </section>
    );
};

export default React.memo(MonthView);
